/**
 * Public Privacy Policy page.
 *
 * Mounted at /privacy via PublicAppLayout so it inherits the dark glass
 * theme + sidebar nav. No auth required — anyone can read it. Bilingual
 * via the existing i18n shell — strings live under the `privacy.*`
 * namespace in en.json + es.json.
 */
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Shield, Mail } from "lucide-react";

const Section = ({ title, children, testId }) => (
  <section className="mb-8" data-testid={testId}>
    <h2
      className="text-base sm:text-lg font-semibold mb-3"
      style={{ color: "var(--color-text-primary)" }}
    >
      {title}
    </h2>
    <div
      className="text-sm leading-relaxed space-y-3"
      style={{ color: "var(--color-text-secondary)" }}
    >
      {children}
    </div>
  </section>
);

const PrivacyPage = () => {
  const { t } = useTranslation();
  // Bumped only when we materially change the policy. Shown at the top so
  // returning users can spot updates at a glance.
  const lastUpdated = t("privacy.lastUpdatedDate", { defaultValue: "April 27, 2026" });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="privacy-page">
      <Helmet>
        <title>{t("privacy.title")} · Evoluras</title>
        <meta name="description" content={t("privacy.metaDescription")} />
      </Helmet>

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "rgba(167,139,250,0.18)" }}
        >
          <Shield className="w-5 h-5" style={{ color: "var(--color-brand-primary)" }} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: "var(--color-text-primary)" }}>
            {t("privacy.title")}
          </h1>
          <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
            {t("privacy.lastUpdated", { date: lastUpdated })}
          </p>
        </div>
      </div>

      <p className="text-sm mb-6 mt-4" style={{ color: "var(--color-text-secondary)" }}>
        {t("privacy.intro")}
      </p>

      {/* Plain-English principles — sets the tone before the formal sections */}
      <div
        className="rounded-2xl p-5 mb-8 backdrop-blur-md"
        style={{
          background: "linear-gradient(135deg, rgba(167,139,250,0.14) 0%, rgba(56,189,248,0.10) 100%)",
          border: "1px solid rgba(167,139,250,0.25)",
        }}
        data-testid="privacy-principles"
      >
        <p className="text-xs uppercase tracking-wide font-semibold mb-2" style={{ color: "var(--color-brand-primary)" }}>
          {t("privacy.principlesLabel")}
        </p>
        <p className="text-sm leading-relaxed italic" style={{ color: "var(--color-text-primary)" }}>
          {t("privacy.principlesBody")}
        </p>
      </div>

      {/* Sections */}
      <Section title={t("privacy.s1Title")} testId="privacy-section-collect">
        <p>{t("privacy.s1Body")}</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>{t("privacy.s1Bullet1")}</li>
          <li>{t("privacy.s1Bullet2")}</li>
          <li>{t("privacy.s1Bullet3")}</li>
          <li>{t("privacy.s1Bullet4")}</li>
        </ul>
      </Section>

      <Section title={t("privacy.s2Title")} testId="privacy-section-use">
        <p>{t("privacy.s2Body")}</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>{t("privacy.s2Bullet1")}</li>
          <li>{t("privacy.s2Bullet2")}</li>
          <li>{t("privacy.s2Bullet3")}</li>
        </ul>
      </Section>

      <Section title={t("privacy.s3Title")} testId="privacy-section-share">
        <p>{t("privacy.s3Body")}</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>{t("privacy.s3Bullet1")}</li>
          <li>{t("privacy.s3Bullet2")}</li>
          <li>{t("privacy.s3Bullet3")}</li>
        </ul>
        <p className="mt-2 font-medium" style={{ color: "var(--color-text-primary)" }}>
          {t("privacy.s3NoSale")}
        </p>
      </Section>

      <Section title={t("privacy.s4Title")} testId="privacy-section-thirdparty">
        <p>{t("privacy.s4Body")}</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>{t("privacy.s4Google")}</li>
          <li>{t("privacy.s4Gemini")}</li>
          <li>{t("privacy.s4Resend")}</li>
          <li>{t("privacy.s4Expo")}</li>
        </ul>
      </Section>

      <Section title={t("privacy.s5Title")} testId="privacy-section-storage">
        <p>{t("privacy.s5Body")}</p>
      </Section>

      <Section title={t("privacy.s6Title")} testId="privacy-section-rights">
        <p>{t("privacy.s6Body")}</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>{t("privacy.s6Bullet1")}</li>
          <li>{t("privacy.s6Bullet2")}</li>
          <li>{t("privacy.s6Bullet3")}</li>
          <li>{t("privacy.s6Bullet4")}</li>
        </ul>
      </Section>

      <Section title={t("privacy.s7Title")} testId="privacy-section-children">
        <p>{t("privacy.s7Body")}</p>
      </Section>

      <Section title={t("privacy.s8Title")} testId="privacy-section-changes">
        <p>{t("privacy.s8Body")}</p>
      </Section>

      <Section title={t("privacy.s9Title")} testId="privacy-section-contact">
        <p>{t("privacy.s9Body")}</p>
        <a
          href="mailto:hello@evoluras.com"
          className="inline-flex items-center gap-1.5 mt-1 text-sm font-medium hover:underline"
          style={{ color: "var(--color-text-interactive)" }}
        >
          <Mail className="w-3.5 h-3.5" />
          hello@evoluras.com
        </a>
      </Section>
    </div>
  );
};

export default PrivacyPage;
